<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12">
      <card-title-row :straightTitle="$i18n.t('sidebar.nmtProfile')" class="py-6"> </card-title-row>
      <sidebar-form :inputs="inputs" :is-valid.sync="isValid" v-model="payload" :is-editing-on="!isDone" ref="form">
        <template #profile="scope">
          <sidebar-geometry-input
            :is-editing-on="true"
            v-bind="scope"
            v-model="payload.geometry"
            :is-valid.sync="isGeometryValid"
          />
        </template>
      </sidebar-form>
      <sidebar-row class="pr-2" border-bottom>
        <v-row no-gutters class="pt-2 pb-5">
          <v-col cols="auto">
            <base-button
              :translation-path="buttonText"
              @click="buttonClickHandler"
              :disabled="isDisabled"
              :loading="loading"
            ></base-button>
          </v-col>
        </v-row>
      </sidebar-row>
      <v-row no-gutters>
        <v-col cols="12">
          <nmt-sidebar-profile
            @showDialog="showCharDialog"
            :statistics="statistics"
            :heights="heights"
            v-if="isDone"
            :length="computedLength"
            :interval="payload.interval"
            @drawMarker="drawMarker"
          ></nmt-sidebar-profile>
        </v-col>
      </v-row>
    </v-col>
    <v-col>
      <nmt-sidebar-profile-chart-dialog
        :is-visible.sync="isNmtChartDialogVisible"
        v-if="isNmtChartDialogLoaded"
        :statistics="statistics"
        :heights="heights"
        :interval="payload.interval"
        :length="computedLength"
      >
      </nmt-sidebar-profile-chart-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { call } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import SidebarGeometryInput from '@/components/SidebarGeometryInput';
import SidebarForm from '@/components/SidebarForm';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'NmtSidebar',
  components: {
    CardTitleRow,
    SidebarGeometryInput,
    SidebarForm,
    SidebarRow,
    NmtSidebarProfile: () => import(/* webpackChunkName: "nmtMapProfile" */ '@/components/NmtSidebarProfile'),
    NmtSidebarProfileChartDialog: () =>
      import(/* webpackChunkName: "NmtSidebarProfileChartDialog" */ '@/components/NmtSidebarProfileChartDialog'),
  },
  data: () => ({
    loading: false,
    heights: undefined,
    statistics: undefined,
    rawStatistics: undefined,
    payload: {
      geometry: undefined,
      interval: undefined,
    },
    isGeometryValid: false,
    isValid: false,
    isNmtChartDialogVisible: false,
    isNmtChartDialogLoaded: false,
    isDone: false,
    dotsMenuItems: [
      {
        name: 'delete',
      },
    ],
  }),
  computed: {
    isDisabled() {
      return !this.isGeometryValid || !this.isValid;
    },
    buttonText() {
      return this.isDone ? 'button.reset' : 'button.generateChart';
    },
    computedLength() {
      return this.rawStatistics?.length?.toFixed(2);
    },
    inputs() {
      return [
        {
          hasPadding: false,
          hasNameColumn: false,
          icon: 'mdi-map-marker',
          geometryType: 'LineString',
          cols: '11',
          name: 'profile',
          hint: this.$i18n.t('sidebar.nmtHint'),
        },
        {
          align: 'baseline',
          name: 'interval',
          translationPath: 'sidebar.interval',
          placeholder: '',
          hint: this.$i18n.t('sidebar.nmtIntervalHint'),
          minValue: 1,
          dataType: {
            name: 'integer',
          },
        },
      ];
    },
  },
  methods: {
    getProfile: call('nmt/getProfile'),
    showCharDialog() {
      this.isNmtChartDialogVisible = true;
      this.isNmtChartDialogLoaded = true;
    },
    buttonClickHandler() {
      if (this.isDone) {
        this.resetChart();
      } else {
        this.generateChart();
      }
    },
    resetChart() {
      this.isDone = false;
      this.payload = {
        geometry: undefined,
        interval: undefined,
      };
      this.$root.$emit('deleteSidebarGeometry');
      // just to be sure
      this.$root.$emit('removeMarker');
      this.$refs.form?.$refs.form?.resetValidation();
    },
    getFixedValue(value) {
      if (value !== undefined) {
        return value.toFixed(2);
      }
    },
    getStatisctis(statsObject) {
      return [
        {
          name: 'length',
          value: this.getFixedValue(statsObject.length),
          translationPath: 'sidebar.profileLength',
          unit: 'm',
        },
        {
          name: 'startHeight',
          value: this.getFixedValue(statsObject.start_height),
          translationPath: 'sidebar.startHeight',
          unit: `m ${this.$i18n.t('sidebar.nmtAboveSeaLevelAbbr')}`,
        },
        {
          name: 'endHeight',
          value: this.getFixedValue(statsObject.end_height),
          translationPath: 'sidebar.endHeight',
          unit: `m ${this.$i18n.t('sidebar.nmtAboveSeaLevelAbbr')}`,
        },
        {
          name: 'meanSlope',
          value: this.getFixedValue(statsObject.mean_slope),
          translationPath: 'sidebar.meanSlope',
          unit: '&permil;',
        },
      ];
    },
    async generateChart() {
      try {
        this.loading = true;
        this.$root.$emit('deactivateSidebarModify');
        const { geometry: line, interval } = this.payload;
        const r = await this.getProfile({ interval, line });
        if (r.data.data.heights?.length > 0) {
          this.rawStatistics = r.data.data.statistics;
          this.statistics = this.getStatisctis(this.rawStatistics);
          this.heights = r.data.data.heights;
          this.loading = false;
          this.isDone = true;
        } else {
          throw new Error(this.$i18n.t('errors.profileNoResults'));
        }
      } catch (error) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: error.message,
        });
        this.loading = false;
      }
    },
    drawMarker(fraction) {
      this.$root.$emit('drawProfileMarker', this.payload.geometry, fraction);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-btn--outlined {
    border-width: 2px;
  }
  .v-slider--horizontal {
    margin: 0px;
    .v-slider__thumb {
      border: 2px solid var(--thumb-color) !important;
    }
  }
  .theme--light.v-label--is-disabled {
    color: rgba(0, 0, 0, 0.6);
  }
  .theme--light.v-input--is-disabled input,
  .theme--light.v-input--is-disabled textarea {
    color: rgba(0, 0, 0, 0.87);
  }
  .theme--light.v-text-field.v-input--is-disabled .v-input__slot::before {
    border-image: unset !important;
  }
}
.border {
  @include border(bottom);
}
</style>
